import { useEffect, useRef } from 'react'
import { Button } from '@heroui/react'
import { useStaffText } from '../ui/StaffText'
import type { Issue } from './issues'

/** How long the list stays after the pointer leaves, to cross the gap into it. */
const LINGER_MS = 200

/**
 * The editor header's "3 to fix": nothing when there is nothing; otherwise a pill
 * whose list opens on hover, focus or tap. The canvas itself stays clean.
 */
export function IssuesIndicator({
  issues,
  open,
  onOpen,
  onPick,
  onOpenPage,
}: {
  issues: readonly Issue[]
  open: boolean
  onOpen: (open: boolean) => void
  /** An issue about an element: select it. */
  onPick: (issue: Issue) => void
  /** A kiosk page nothing opens: add a button for it to the menu. */
  onOpenPage: (pageId: string) => void
}) {
  const { say } = useStaffText()
  const anchor = useRef<HTMLDivElement>(null)
  const badge = useRef<HTMLButtonElement>(null)
  const linger = useRef(0)
  // Focus handed back to the pill after a pick does not open the list again.
  const quiet = useRef(false)
  const close = (refocus: boolean) => {
    onOpen(false)
    if (!refocus) return
    quiet.current = true
    badge.current?.focus()
  }
  const closeRef = useRef(close)
  closeRef.current = close

  useEffect(() => () => window.clearTimeout(linger.current), [])
  useEffect(() => {
    if (!open) return
    const down = (e: PointerEvent) => !anchor.current?.contains(e.target as Node) && closeRef.current(false)
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Handled here: the editor's own Escape would also let go of the selection.
      e.preventDefault()
      closeRef.current(true)
    }
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  if (issues.length === 0) return null
  const blocking = issues.some((i) => i.blocks)
  const show = () => {
    window.clearTimeout(linger.current)
    onOpen(true)
  }
  return (
    <div
      className="studio-probs-anchor"
      ref={anchor}
      onMouseEnter={show}
      onMouseLeave={() => {
        linger.current = window.setTimeout(() => onOpen(false), LINGER_MS)
      }}
      onBlur={(e) => {
        if (!anchor.current?.contains(e.relatedTarget as Node)) onOpen(false)
      }}
    >
      <button
        ref={badge}
        type="button"
        className="studio-probs"
        data-blocks={blocking || undefined}
        aria-expanded={open}
        aria-controls="studio-issues"
        onFocus={() => {
          if (quiet.current) quiet.current = false
          else show()
        }}
        onClick={show}
      >
        <span className="studio-probs__dot" aria-hidden="true" />
        {say('designs.page.toFix', { n: issues.length })}
      </button>
      {open && (
        <ul id="studio-issues" className="studio-issues" aria-label={say('designs.page.layerToFix')}>
          {issues.map((issue, i) => (
            <li key={`${i}:${issue.text}`} data-blocks={issue.blocks || undefined}>
              <span className="studio-issues__dot" aria-hidden="true" title={issue.blocks ? say('designs.issue.blocks') : undefined} />
              {issue.blocks && <span className="sr-only">{say('designs.issue.blocks')}: </span>}
              {issue.ids.length > 0 ? (
                <button
                  type="button"
                  className="studio-issues__text"
                  onClick={() => {
                    close(true)
                    onPick(issue)
                  }}
                >
                  {issue.text}
                </button>
              ) : (
                <span className="studio-issues__text">{issue.text}</span>
              )}
              {(issue.n ?? 1) > 1 && <span className="studio-issues__n">×{issue.n}</span>}
              {issue.openPage && (
                <Button
                  variant="secondary"
                  size="sm"
                  onPress={() => {
                    close(true)
                    onOpenPage(issue.openPage!)
                  }}
                >
                  {say('designs.warn.addOpener')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
